import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Store, Mail, Lock, ArrowRight, AlertCircle, Eye, EyeOff, Sparkles, TrendingUp, Users, DollarSign } from "lucide-react";
import { loginReseller } from "@/shared/api/auth";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

export default function ResellerLogin() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [focused, setFocused] = useState("");
  const containerRef = useRef(null);
  const [mouse, setMouse] = useState({ x: 50, y: 50 });

  useEffect(() => {
    const h = (e) => {
      if (!containerRef.current) return;
      const r = containerRef.current.getBoundingClientRect();
      setMouse({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
    };
    window.addEventListener("mousemove", h);
    return () => window.removeEventListener("mousemove", h);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) { setError(t("pleaseEnterBothEmailAndPassword")); return; }
    setLoading(true);
    try {
      await loginReseller(email, password);
      navigate("/ResellerDashboard");
    } catch (err) {
      setError(err.message || t("loginFailed"));
      setLoading(false);
    }
  };

  return (
    <div ref={containerRef} style={{ minHeight: "100vh", background: "#0E0900", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "'Inter', -apple-system, sans-serif", position: "relative", overflow: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

        .rl-orb { position: absolute; border-radius: 50%; pointer-events: none; filter: blur(90px); transition: all 1.4s cubic-bezier(0.23,1,0.32,1); }

        /* Dot grid */
        .rl-dotgrid { position: fixed; inset: 0; pointer-events: none; background-image: radial-gradient(rgba(251,146,60,0.08) 1.5px, transparent 1.5px); background-size: 30px 30px; }

        .rl-card {
          background: linear-gradient(155deg, rgba(20,12,2,0.97) 0%, rgba(12,7,1,0.99) 100%);
          border: 1px solid rgba(251,146,60,0.15);
          border-radius: 24px; padding: 44px;
          box-shadow: 0 0 0 1px rgba(251,146,60,0.05), 0 40px 100px rgba(0,0,0,0.75), inset 0 1px 0 rgba(255,255,255,0.04);
          position: relative; overflow: hidden;
        }
        .rl-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px; background: linear-gradient(90deg, transparent 0%, rgba(251,146,60,0.65) 35%, rgba(245,158,11,0.55) 65%, transparent 100%); }

        /* Inner shimmer */
        .rl-shimmer { position: absolute; top: 0; left: -100%; width: 80%; height: 100%; background: linear-gradient(105deg,transparent 40%,rgba(251,146,60,0.035) 50%,transparent 60%); animation: rl-shim 4.5s ease-in-out infinite; pointer-events: none; }
        @keyframes rl-shim { 0%{left:-100%} 100%{left:120%} }

        /* Input - DARK ORANGE */
        .rl-input {
          width: 100%; height: 52px;
          background: rgba(30,14,2,0.9) !important;
          border: 1.5px solid rgba(251,146,60,0.14) !important;
          border-radius: 12px;
          color: #FED7AA !important;
          font-size: 14px; font-family: 'Inter', sans-serif;
          padding: 0 46px !important;
          outline: none !important;
          transition: all 0.25s ease;
          -webkit-appearance: none;
          caret-color: #F97316;
        }
        .rl-input::placeholder { color: rgba(253,186,116,0.2) !important; }
        .rl-input:hover { border-color: rgba(251,146,60,0.3) !important; background: rgba(35,16,2,0.95) !important; }
        .rl-input:focus {
          border-color: rgba(251,146,60,0.55) !important;
          background: rgba(40,18,2,0.95) !important;
          box-shadow: 0 0 0 4px rgba(251,146,60,0.09), 0 2px 16px rgba(251,146,60,0.06) !important;
        }

        .rl-icon { position: absolute; left: 15px; top: 50%; transform: translateY(-50%); pointer-events: none; transition: color 0.25s; }
        .rl-eye { position: absolute; right: 14px; top: 50%; transform: translateY(-50%); background: none; border: none; cursor: pointer; color: rgba(253,186,116,0.25); padding: 4px; border-radius: 6px; display: flex; transition: all 0.2s; }
        .rl-eye:hover { color: #FB923C; background: rgba(251,146,60,0.1); }

        /* Button */
        .rl-btn {
          width: 100%; height: 52px;
          background: linear-gradient(135deg, #92400E 0%, #C2410C 40%, #EA580C 80%, #F97316 100%);
          border: none; border-radius: 12px;
          color: white; font-size: 15px; font-weight: 600;
          font-family: 'Inter', sans-serif;
          cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px;
          transition: all 0.25s cubic-bezier(0.4,0,0.2,1);
          box-shadow: 0 4px 22px rgba(234,88,12,0.38), inset 0 1px 0 rgba(255,255,255,0.1);
          position: relative; overflow: hidden;
        }
        .rl-btn-shine { position: absolute; top: 0; left: -100%; width: 60%; height: 100%; background: linear-gradient(90deg,transparent,rgba(255,255,255,0.1),transparent); transform: skewX(-20deg); transition: left 0.55s ease; }
        .rl-btn:hover:not(:disabled) .rl-btn-shine { left: 150%; }
        .rl-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 8px 32px rgba(234,88,12,0.5); background: linear-gradient(135deg,#B45309 0%,#D97706 40%,#F97316 80%,#FB923C 100%); }
        .rl-btn:active:not(:disabled) { transform: translateY(0); }
        .rl-btn:disabled { opacity: 0.6; cursor: not-allowed; }

        .rl-spin { width: 19px; height: 19px; border: 2.5px solid rgba(255,255,255,0.25); border-top-color: white; border-radius: 50%; animation: rl-rot 0.65s linear infinite; }
        @keyframes rl-rot { to { transform: rotate(360deg); } }

        .rl-label { font-size: 11.5px; font-weight: 600; color: rgba(253,186,116,0.38); letter-spacing: 0.07em; text-transform: uppercase; margin-bottom: 8px; display: block; font-family: 'Inter', sans-serif; }
        .rl-link { color: #FB923C; text-decoration: none; font-size: 13px; font-weight: 500; transition: color 0.2s; }
        .rl-link:hover { color: #FDBA74; }

        .rl-benefit {
          display: flex; align-items: center; gap: 10px;
          padding: 10px 14px; border-radius: 11px;
          background: rgba(251,146,60,0.04);
          border: 1px solid rgba(251,146,60,0.08);
          cursor: default; transition: all 0.25s ease;
        }
        .rl-benefit:hover { background: rgba(251,146,60,0.08); border-color: rgba(251,146,60,0.18); transform: translateX(4px); }

        .rl-badge { display: inline-flex; align-items: center; gap: 6px; padding: 5px 12px; border-radius: 100px; background: rgba(251,146,60,0.1); border: 1px solid rgba(251,146,60,0.2); font-size: 11px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; color: #FB923C; font-family: 'Inter', sans-serif; }
      `}</style>

      {/* Dynamic orbs */}
      <div className="rl-orb" style={{ width: 550, height: 550, background: "radial-gradient(circle,rgba(234,88,12,0.13) 0%,transparent 68%)", top: `${mouse.y * 0.22}%`, left: `${mouse.x * 0.22}%` }} />
      <div className="rl-orb" style={{ width: 380, height: 380, background: "radial-gradient(circle,rgba(245,158,11,0.09) 0%,transparent 68%)", bottom: "5%", right: "5%", transitionDuration: "2s" }} />
      <div className="rl-dotgrid" />

      {/* Top bar */}
      <motion.div
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ position: "fixed", top: 0, left: 0, right: 0, height: 46, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 28px", borderBottom: "1px solid rgba(251,146,60,0.08)", backdropFilter: "blur(24px)", zIndex: 50, background: "rgba(10,6,0,0.7)" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: "linear-gradient(135deg,#92400E,#F97316)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Store size={15} color="white" />
          </div>
          <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 16, color: "#FED7AA", letterSpacing: "-0.02em" }}>1401</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#4ADE80", boxShadow: "0 0 7px #4ADE80" }} />
          <span style={{ fontFamily: "monospace", fontSize: 10.5, color: "rgba(253,186,116,0.3)", letterSpacing: "0.06em" }}>PARTNER NETWORK LIVE</span>
        </div>
      </motion.div>

      {/* Two column layout */}
      <div style={{ display: "flex", width: "100%", maxWidth: 1000, gap: 60, alignItems: "center", paddingTop: 46 }}>

        {/* Left */}
        <motion.div style={{ flex: 1 }} initial={{ opacity: 0, x: -28 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.65, ease: [0.22,1,0.36,1] }}>
          <div className="rl-badge" style={{ marginBottom: 22 }}>
            <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#F97316" }} />
            Partner Portal
          </div>

          <h1 style={{ fontFamily: "'Inter', sans-serif", fontSize: 38, fontWeight: 800, color: "#FEF3C7", letterSpacing: "-0.038em", lineHeight: 1.18, margin: "0 0 14px" }}>
            Grow your<br />
            <span style={{ background: "linear-gradient(135deg,#F97316,#FBBF24)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>reseller</span>{" "}network<br />with 1401.
          </h1>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 14.5, color: "rgba(253,186,116,0.42)", lineHeight: 1.7, margin: "0 0 32px", maxWidth: 330 }}>
            Access your partner dashboard, manage clients, track commissions, and scale your operations effortlessly.
          </p>

          {/* Benefits */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {[
              { icon: DollarSign, label: "Real-time commission tracking & payouts", color: "#FB923C" },
              { icon: Users, label: "Client & sub-reseller management", color: "#FBBF24" },
              { icon: TrendingUp, label: "Sales analytics & growth reports", color: "#34D399" },
            ].map((b, i) => (
              <motion.div key={i} className="rl-benefit" initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.08 }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: `${b.color}18`, border: `1px solid ${b.color}25`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <b.icon size={15} color={b.color} />
                </div>
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 13.5, color: "rgba(253,186,116,0.55)", fontWeight: 500 }}>{b.label}</span>
              </motion.div>
            ))}
          </div>

          {/* Testimonial */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
            style={{ marginTop: 28, padding: "18px 20px", background: "rgba(251,146,60,0.04)", borderRadius: 14, border: "1px solid rgba(251,146,60,0.1)" }}>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: "rgba(253,186,116,0.45)", lineHeight: 1.65, margin: "0 0 12px", fontStyle: "italic" }}>
              "Best reseller program in the market. Commissions are always on time and the dashboard is excellent."
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 30, height: 30, borderRadius: "50%", background: "linear-gradient(135deg,#92400E,#F97316)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, color: "white", fontWeight: 600 }}>S</div>
              <div>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12.5, fontWeight: 600, color: "rgba(253,186,116,0.6)", margin: 0 }}>Sara Ahmad</p>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11.5, color: "rgba(253,186,116,0.28)", margin: 0 }}>Gold Partner · 3 yrs</p>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Right form */}
        <motion.div style={{ width: 420, flexShrink: 0 }} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65, ease: [0.22,1,0.36,1], delay: 0.1 }}>
          <div className="rl-card">
            <div className="rl-shimmer" />

            <div style={{ marginBottom: 30 }}>
              <motion.div whileHover={{ scale: 1.07, rotate: 5 }} transition={{ type: "spring", stiffness: 380, damping: 14 }}
                style={{ width: 52, height: 52, borderRadius: 14, background: "linear-gradient(135deg,#92400E,#EA580C,#F97316)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20, boxShadow: "0 8px 26px rgba(234,88,12,0.4), 0 0 0 1px rgba(251,146,60,0.2)", cursor: "default" }}>
                <Store size={22} color="white" />
              </motion.div>
              <h2 style={{ fontFamily: "'Inter', sans-serif", fontSize: 22, fontWeight: 700, color: "#FEF3C7", margin: 0, letterSpacing: "-0.025em" }}>{t("resellerPanel")}</h2>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13.5, color: "rgba(253,186,116,0.32)", margin: "5px 0 0" }}>{t("partnerPortalAccess")}</p>
            </div>

            <form onSubmit={handleLogin} style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              <AnimatePresence>
                {error && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} style={{ overflow: "hidden" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "11px 14px", borderRadius: 11, background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.15)", color: "#FCA5A5", fontFamily: "'Inter', sans-serif", fontSize: 13 }}>
                      <AlertCircle size={14} style={{ flexShrink: 0, color: "#F87171" }} />
                      {error}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div>
                <label className="rl-label">{t("emailAddress")}</label>
                <div style={{ position: "relative" }}>
                  <Mail size={16} className="rl-icon" style={{ color: focused === "email" ? "#F97316" : "rgba(253,186,116,0.2)" }} />
                  <input type="email" className="rl-input" placeholder="reseller@company.com" value={email} onChange={e => setEmail(e.target.value)} onFocus={() => setFocused("email")} onBlur={() => setFocused("")} required />
                </div>
              </div>

              <div>
                <label className="rl-label">{t("password")}</label>
                <div style={{ position: "relative" }}>
                  <Lock size={16} className="rl-icon" style={{ color: focused === "pass" ? "#F97316" : "rgba(253,186,116,0.2)" }} />
                  <input type={showPw ? "text" : "password"} className="rl-input" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} onFocus={() => setFocused("pass")} onBlur={() => setFocused("")} style={{ paddingRight: 46 }} required />
                  <button type="button" className="rl-eye" onClick={() => setShowPw(!showPw)}>
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontFamily: "'Inter', sans-serif", fontSize: 13, color: "rgba(253,186,116,0.35)" }}>
                  <input type="checkbox" style={{ accentColor: "#F97316" }} />
                  {t("rememberMe")}
                </label>
                <a href="#" className="rl-link">{t("forgotPassword")}</a>
              </div>

              <motion.button type="submit" className="rl-btn" disabled={loading} whileTap={!loading ? { scale: 0.98 } : {}} style={{ marginTop: 4 }}>
                <div className="rl-btn-shine" />
                {loading ? (<><div className="rl-spin" />{t("signingIn")}</>) : (<>{t("signIn")}<ArrowRight size={15} /></>)}
              </motion.button>
            </form>

            <div style={{ height: 1, background: "rgba(251,146,60,0.08)", margin: "26px 0 18px" }} />
            <p style={{ textAlign: "center", fontFamily: "'Inter', sans-serif", fontSize: 13, color: "rgba(253,186,116,0.25)" }}>
              {t("needHelpContact")} <a href="mailto:support@1401.com" className="rl-link">support@1401.com</a>
            </p>
          </div>
          <p style={{ textAlign: "center", fontFamily: "'Inter', sans-serif", fontSize: 12, color: "rgba(253,186,116,0.12)", marginTop: 16 }}>© {new Date().getFullYear()} 1401 · All rights reserved</p>
        </motion.div>
      </div>
    </div>
  );
}